"use client";
import React, { useState, useEffect, useRef } from "react";
import type { ComponentType, SVGProps } from "react";
import AppImage from "@/components/ui/AppImage";
import { useHomeLanguage } from "./HomeLanguageContext";
import {
  BriefcaseIcon,
  ChartBarSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  UserGroupIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";

type TabItem = {
  id: TabId;
  label: string;
  desc: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

type TabId = "hrm" | "accounting" | "mobile" | "webapp" | "crm";

const tabs: TabItem[] = [
  { id: "hrm", label: "Dashboard HRM", Icon: UserGroupIcon, desc: "SDM Lengkap" },
  { id: "accounting", label: "Dashboard Accounting", Icon: ChartBarSquareIcon, desc: "Pembukuan Otomatis" },
  { id: "mobile", label: "Android/iOS", Icon: DevicePhoneMobileIcon, desc: "Aplikasi User/Staff" },
  { id: "webapp", label: "Web App", Icon: ComputerDesktopIcon, desc: "Browser" },
  { id: "crm", label: "CRM", Icon: BriefcaseIcon, desc: "Kelola Pelanggan" },
];


const tabImages: Record<TabId, {src: string;alt: string;}[]> = {
  hrm: [
  { src: "/assets/optimized/kolabo-hrm-1-7bd06d649d.webp", alt: "Kolabo HRM dashboard 1" },
  { src: "/assets/optimized/kolabo-hrm-2-2353ac33b0.webp", alt: "Kolabo HRM dashboard 2" },
  { src: "/assets/optimized/kolabo-hrm-3-ca1abdcb6a.webp", alt: "Kolabo HRM dashboard 3" },
  { src: "/assets/optimized/kolabo-hrm-4-8dfbd7c13d.webp", alt: "Kolabo HRM dashboard 4" },
  { src: "/assets/optimized/kolabo-hrm-5-af4985799e.webp", alt: "Kolabo HRM dashboard 5" },
  { src: "/assets/optimized/kolabo-hrm9-e0bcbc059e.webp", alt: "Kolabo HRM dashboard 6" },
  { src: "/assets/optimized/kolabo-hrm-6-1b8fe9827b.webp", alt: "Kolabo HRM dashboard 7" },
  { src: "/assets/optimized/kolabo-hrm-7-141e1affc8.webp", alt: "Kolabo HRM dashboard 8" },
  { src: "/assets/optimized/kolabo-hrm-8-490869557e.webp", alt: "Kolabo HRM dashboard 9" },
  { src: "/assets/optimized/kolabo-hrm-9-69a744a5f5.webp", alt: "Kolabo HRM dashboard 10" },
  { src: "/assets/optimized/kolabo-hrm-10-aa227f28ea.webp", alt: "Kolabo HRM dashboard 11" },
  { src: "/assets/optimized/kolabo-hrm-11-001d5eeb2a.webp", alt: "Kolabo HRM dashboard 12" }],

  accounting: [
  { src: "/assets/optimized/acc1-fffec54277.webp", alt: "Kolabo Accounting jurnal umum" },
  { src: "/assets/optimized/acc2-e0bdb64c86.webp", alt: "Kolabo Accounting laporan laba rugi" },
  { src: "/assets/optimized/acc3-c34d88ba83.webp", alt: "Kolabo Accounting neraca keuangan" },
  { src: "/assets/optimized/acc4-5ee1a746ea.webp", alt: "Kolabo Accounting arus kas" },
  { src: "/assets/optimized/acc5-04a08cc22d.webp", alt: "Kolabo Accounting invoice dan tagihan" },
  { src: "/assets/optimized/acc6-1979aa11e4.webp", alt: "Kolabo Accounting rekonsiliasi bank" },
  { src: "/assets/optimized/acc7-ba4a47e825.webp", alt: "Kolabo Accounting dashboard utama" }],

  crm: [
  { src: "/assets/optimized/kolabo-crm-36fb578b20.webp", alt: "Kolabo CRM tampilan utama" },
  { src: "/assets/optimized/kolabo-crm-1-4dfd8d0633.webp", alt: "Kolabo CRM pipeline deals" },
  { src: "/assets/optimized/kolabo-crm-2-c100b25769.webp", alt: "Kolabo CRM detail client" },
  { src: "/assets/optimized/kolabo-crm-3-a9da9692f8.webp", alt: "Kolabo CRM analytics" }],

  mobile: [
  { src: "/assets/optimized/kolabo-app-mockup-1-6dedc41089.webp", alt: "Kolabo mobile app overview tampilan lengkap" },
  { src: "/assets/optimized/kolabo-app-mockup-2-4edeff74a8.webp", alt: "Kolabo aplikasi mobile absensi" },
  { src: "/assets/optimized/kolabo-app-mockup-3-c5915da986.webp", alt: "Kolabo mobile app dashboard" },
  { src: "/assets/optimized/kolabo-app-mockup-4-ef9e6fa852.webp", alt: "Kolabo mobile app HR tools" },
  { src: "/assets/optimized/kolabo-app-mockup-5-60fc83d60a.webp", alt: "Kolabo mobile app tugas dan proyek" },
  { src: "/assets/optimized/kolabo-app-mockup-6-d4764e9ddc.webp", alt: "Kolabo mobile app laporan" },
  { src: "/assets/optimized/kolabo-app-mockup-7-14ce9148dd.webp", alt: "Kolabo mobile app dashboard 7" },
  { src: "/assets/optimized/kolabo-app-mockup-8-512d840104.webp", alt: "Kolabo mobile app dashboard 8" },
  { src: "/assets/optimized/kolabo-app-mockup-9-f5778dc4c8.webp", alt: "Kolabo mobile app dashboard 9" },
  { src: "/assets/optimized/kolabo-app-mockup-10-8468410128.webp", alt: "Kolabo mobile app dashboard 10" },
  { src: "/assets/optimized/kolabo-app-mockup-11-fca4a93071.webp", alt: "Kolabo mobile app dashboard 11" },
  { src: "/assets/optimized/kolabo-app-mockup-12-3249eb6e0a.webp", alt: "Kolabo mobile app dashboard 12" },
  { src: "/assets/optimized/kolabo-app-mockup-13-1193127209.webp", alt: "Kolabo mobile app dashboard 13" },
  { src: "/assets/optimized/kolabo-app-mockup-14-4cad12459e.webp", alt: "Kolabo mobile app dashboard 14" },
  { src: "/assets/optimized/kolabo-app-mockup-15-0f7ae3bae1.webp", alt: "Kolabo mobile app dashboard 15" },
  { src: "/assets/optimized/kolabo-app-mockup-16-3728cfd002.webp", alt: "Kolabo mobile app dashboard 16" }],

  webapp: [
  { src: "/assets/optimized/kolabo-webapp-mockup-1-9c0059dddb.webp", alt: "Kolabo web app 1" },
  { src: "/assets/optimized/kolabo-webapp-mockup-2-86ba1461ed.webp", alt: "Kolabo web app 2" },
  { src: "/assets/optimized/kolabo-webapp-mockup-3-1b74650a8d.webp", alt: "Kolabo web app 3" },
  { src: "/assets/optimized/kolabo-webapp-mockup-4-e67977dc40.webp", alt: "Kolabo web app 4" },
  { src: "/assets/optimized/kolabo-webapp-mockup-5-1a788255d7.webp", alt: "Kolabo web app 5" },
  { src: "/assets/optimized/kolabo-webapp-mockup-6-816746d7b7.webp", alt: "Kolabo web app 6" },
  { src: "/assets/optimized/kolabo-webapp-mockup-7-610c937c1d.webp", alt: "Kolabo web app 7" },
  { src: "/assets/optimized/kolabo-webapp-mockup-8-010701f4b7.webp", alt: "Kolabo web app 8" }]

};

const tabHighlightsId: Record<TabId, string[]> = {
  hrm: ["Absensi GPS & selfie", "Payroll & PPh21", "Cuti, lembur, approval", "KPI & performance review"],
  accounting: ["Jurnal otomatis", "Laporan laba rugi", "Neraca & arus kas", "Rekonsiliasi transaksi"],
  crm: ["Lead & pipeline sales", "Follow-up terjadwal", "Riwayat komunikasi", "Konversi deal lebih terukur"],
  mobile: ["Absensi & approval cepat", "Notifikasi instan", "Pantau tim saat mobile", "Akses Android & iOS"],
  webapp: ["Dashboard manajemen", "Data lintas divisi", "Laporan eksekutif", "Akses via browser"]
};

const tabHighlightsEn: Record<TabId, string[]> = {
  hrm: ["GPS & selfie attendance", "Payroll & tax support", "Leave, overtime, approvals", "KPI & performance review"],
  accounting: ["Automated journals", "Profit & loss reports", "Balance sheet & cashflow", "Transaction reconciliation"],
  crm: ["Leads & sales pipeline", "Scheduled follow-up", "Communication history", "Measurable deal conversion"],
  mobile: ["Fast attendance & approvals", "Instant notifications", "Manage teams on the go", "Android & iOS access"],
  webapp: ["Management dashboard", "Cross-division data", "Executive reporting", "Browser-based access"],
};

const highlightColorSets = [
  {
    card: "bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 border-emerald-200/80 text-emerald-900",
    dot: "bg-emerald-500",
  },
  {
    card: "bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50 border-sky-200/80 text-sky-900",
    dot: "bg-sky-500",
  },
  {
    card: "bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 border-amber-200/80 text-amber-900",
    dot: "bg-amber-500",
  },
  {
    card: "bg-gradient-to-br from-violet-50 via-fuchsia-50 to-pink-50 border-violet-200/80 text-violet-900",
    dot: "bg-violet-500",
  },
];

const platforms = [
{ icon: "/assets/optimized/icon-btn-googleplaystore-4a7386020b.webp", label: "Google Play", sub: "Android", href: "https://play.google.com/store/apps/details?id=com.langit.kolabo&hl=id", logoClass: "h-11 sm:h-12" },
{ icon: "/assets/optimized/icon-btn-appstore-9420c26ca5.webp", label: "App Store", sub: "iOS", href: "https://apps.apple.com/us/app/kolabo-hr-collaboration-app/id6520393208?platform=iphone", logoClass: "h-11 sm:h-12" },
{ icon: "/assets/optimized/icon-btn-windows-3257f3551d.webp", label: "Windows", sub: "Desktop", href: "#", logoClass: "h-10 sm:h-11" },
{ icon: "/assets/optimized/icon-btn-download-ea68211935.webp", label: "Web App", sub: "Browser", href: "https://app.kolabo.biz", logoClass: "h-10 sm:h-11" }];


export default function DashboardShowcase() {
  const { lang } = useHomeLanguage();
  const [activeTab, setActiveTab] = useState<TabId>("hrm");
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);
  const [openedTabs, setOpenedTabs] = useState<Record<TabId, boolean>>({
    hrm: true,
    accounting: false,
    mobile: false,
    webapp: false,
    crm: false,
  });
  const [loadedThumbsByTab, setLoadedThumbsByTab] = useState<Record<TabId, Record<number, true>>>({
    hrm: { 0: true },
    accounting: {},
    mobile: {},
    webapp: {},
    crm: {},
  });
  const sectionRef = useRef<HTMLDivElement>(null);

  const images = openedTabs[activeTab] ? tabImages[activeTab] : [];
  const safeActiveIdx = Math.min(activeIdx, Math.max(images.length - 1, 0));
  const isMobileTab = activeTab === "mobile";
  const tabLabelsByLang: Record<TabId, string> = {
    hrm: lang === "id" ? "Dashboard HRM" : "HRM Dashboard",
    accounting: lang === "id" ? "Dashboard Accounting" : "Accounting Dashboard",
    mobile: "Android/iOS",
    webapp: "Web App",
    crm: "CRM",
  };
  const tabDescByLang: Record<TabId, string> = {
    hrm: lang === "id" ? "SDM Lengkap" : "People Ops",
    accounting: lang === "id" ? "Pembukuan Otomatis" : "Auto Bookkeeping",
    mobile: lang === "id" ? "Aplikasi User/Staff" : "User/Staff App",
    webapp: lang === "id" ? "Browser" : "Browser",
    crm: lang === "id" ? "Kelola Pelanggan" : "Customer Management",
  };
  const activeHighlights = lang === "id" ? tabHighlightsId[activeTab] : tabHighlightsEn[activeTab];

  const goToIdx = (idx: number) => {
    setActiveIdx(idx);
  };

  useEffect(() => {
    setActiveIdx(0);
    setLightboxIdx(null);
  }, [activeTab]);

  useEffect(() => {
    if (!openedTabs[activeTab] || images.length === 0) return;
    setLoadedThumbsByTab((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], [safeActiveIdx]: true },
    }));
  }, [activeTab, safeActiveIdx, images.length, openedTabs[activeTab]]);

  useEffect(() => {
    if (lightboxIdx === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightboxIdx(null);
      if (e.key === "ArrowLeft") {
        setLightboxIdx((prev) => prev === null ? null : (prev - 1 + images.length) % images.length);
      }
      if (e.key === "ArrowRight") {
        setLightboxIdx((prev) => prev === null ? null : (prev + 1) % images.length);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightboxIdx, images.length]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("revealed");
        });
      },
      { threshold: 0.08 }
    );
    sectionRef.current?.querySelectorAll(".animate-on-scroll").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="py-24 lg:py-28 bg-white relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 grid-pattern opacity-20 pointer-events-none" />
      {/* Glow orbs */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-primary/[0.03] rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-secondary/[0.03] rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-12 animate-on-scroll">
          <span className="section-label mb-6 inline-flex">
            <span className="w-1.5 h-1.5 rounded-full bg-primary pulse-dot inline-block" />
            {lang === "id" ? "Kolabo adalah Solusi" : "Kolabo is the Solution"}
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-[3.25rem] font-bold text-fg leading-[1.1] tracking-[-0.03em] mb-5 mt-5">
            {lang === "id" ? "Kolabo sudah menjadi solusi bagi banyak tim dan perusahaan" : "Kolabo already powers many teams and companies"}
          </h2>
          <p className="text-fg-muted text-[17px] max-w-2xl mx-auto leading-relaxed">
            {lang === "id"
              ? "Gunakan Kolabo sekarang untuk meningkatkan produktivitas dan kinerja tim pada perusahaan Anda."
              : "Use Kolabo to improve team productivity and company performance."}
          </p>
        </div>

        {/* Tab navigation — pill style */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-7 animate-on-scroll">
          {tabs.map((tab) => {
          const Icon = tab.Icon;
          return (
          <button
            key={tab.id}
            onClick={() => {
              setOpenedTabs((prev) => ({ ...prev, [tab.id]: true }));
              setLoadedThumbsByTab((prev) => ({
                ...prev,
                [tab.id]: { ...prev[tab.id], 0: true },
              }));
              setActiveTab(tab.id);
            }}
            className={`group flex items-center gap-2.5 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 ${
            activeTab === tab.id ?
            "glass-green text-white shadow-brand-lg" :
            "bg-white border border-stone-200 text-fg-muted hover:text-fg hover:border-primary/25 hover:bg-primary/4"}`
            }>
              <span className="flex items-center justify-center">
                <Icon className={`w-4 h-4 ${activeTab === tab.id ? "text-white" : "text-primary"}`} aria-hidden="true" />
              </span>
              <div className="text-left">
                <div className="text-[13px]">{tabLabelsByLang[tab.id]}</div>
                <div className={`text-[12px] font-semibold tracking-[0.02em] ${activeTab === tab.id ? "text-white/95" : "text-fg-muted/70"}`}>
                  {tabDescByLang[tab.id]}
                </div>
              </div>
            </button>
          );
          })}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5 mb-8 animate-on-scroll">
          {(activeHighlights || []).map((item, i) => {
          const styleSet = highlightColorSets[i % highlightColorSets.length];
          return (
          <div
            key={i}
            className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-[12px] font-semibold shadow-sm hover:shadow-md transition-all duration-300 ${styleSet.card}`}
          >
              <span className={`w-2 h-2 rounded-full shrink-0 shadow-sm ${styleSet.dot}`} />
              <span>{item}</span>
            </div>
          );
          })}
        </div>

        {/* Main showcase */}
        <div className="relative animate-on-scroll scale-in">
          {/* Glow behind showcase */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.05] to-secondary/[0.03] rounded-3xl blur-2xl scale-105 pointer-events-none" />

          {/* Main image */}
          <div
            className="relative rounded-3xl overflow-hidden border border-stone-200/80 shadow-2xl bg-stone-100 cursor-zoom-in w-full aspect-[16/9]"
          >
            {images.length > 0 && (
            <div className="absolute inset-0" onClick={() => setLightboxIdx(safeActiveIdx)}>
              <AppImage
                key={`${activeTab}-${safeActiveIdx}`}
                src={images[safeActiveIdx].src}
                alt={images[safeActiveIdx].alt}
                fill
                className={isMobileTab ? "object-contain object-center bg-white" : "object-cover object-top bg-white"}
                sizes="(max-width: 1280px) 100vw, 1280px"
                priority={activeTab === "hrm" && safeActiveIdx === 0}
                loading={safeActiveIdx === 0 ? "eager" : "lazy"}
              />
            </div>
            )}

            {/* Overlay gradient */}
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-fg/45 to-transparent pointer-events-none" />

            {/* Navigation arrows */}
            <button
              onClick={() => images.length > 0 && goToIdx((safeActiveIdx - 1 + images.length) % images.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 backdrop-blur-md border border-stone-200 text-fg flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300"
              aria-label={lang === "id" ? "Gambar sebelumnya" : "Previous image"}>
              
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button
              onClick={() => images.length > 0 && goToIdx((safeActiveIdx + 1) % images.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 backdrop-blur-md border border-stone-200 text-fg flex items-center justify-center hover:bg-white hover:scale-110 transition-all duration-300"
              aria-label={lang === "id" ? "Gambar berikutnya" : "Next image"}>
              
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>

            {/* Progress dots */}
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
              {images.map((_, i) =>
              <button
                key={i}
                onClick={() => goToIdx(i)}
                className={`rounded-full transition-all duration-400 ${
                i === safeActiveIdx ? "w-6 h-2 bg-primary" : "w-2 h-2 bg-primary/30 hover:bg-primary/60"}`
                }
                aria-label={`${lang === "id" ? "Gambar" : "Image"} ${i + 1}`} />

              )}
            </div>

            {/* Slide counter */}
            <div className="absolute bottom-5 right-5 bg-white/90 backdrop-blur-sm text-fg/85 text-xs font-mono px-3 py-1.5 rounded-full border border-stone-200">
              {images.length > 0 ? `${safeActiveIdx + 1} / ${images.length}` : "0 / 0"}
            </div>
            <button
              type="button"
              onClick={() => images.length > 0 && setLightboxIdx(safeActiveIdx)}
              className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm text-fg text-xs px-3 py-1.5 rounded-full border border-stone-200 hover:border-primary/35 hover:text-primary transition-all">
              {lang === "id" ? "Klik untuk perbesar" : "Click to enlarge"}
            </button>
          </div>

          {/* Thumbnail strip */}
          <div className="flex gap-2.5 mt-4 overflow-x-auto pb-2 scrollbar-hide">
            {images.map((img, i) =>
            <button
              key={`${activeTab}-thumb-${i}`}
              onClick={() => {
                setLoadedThumbsByTab((prev) => ({
                  ...prev,
                  [activeTab]: { ...prev[activeTab], [i]: true },
                }));
                goToIdx(i);
              }}
              className={`relative w-20 h-14 rounded-xl overflow-hidden shrink-0 border-2 transition-all duration-400 ${
              i === safeActiveIdx ?
              "border-primary shadow-brand scale-105" :
              "border-transparent opacity-50 hover:opacity-80 hover:scale-102"}`
              }
              aria-label={`Tampilkan gambar ${i + 1}`}>
              {loadedThumbsByTab[activeTab]?.[i] || Math.abs(i - safeActiveIdx) <= 1 ? (
                <AppImage
                  src={img.src}
                  alt={img.alt}
                  fill
                  className={isMobileTab ? "object-contain object-center bg-white" : "object-cover object-top bg-white"}
                  sizes="80px"
                  loading="lazy"
                />
              ) : (
                <div className="absolute inset-0 bg-stone-100 flex items-center justify-center text-[11px] font-semibold text-stone-500">
                  {i + 1}
                </div>
              )}
              </button>
            )}
          </div>
        </div>

        {/* Platform download section */}
        <div id="download" className="mt-12 bg-white rounded-[30px] p-7 sm:p-8 lg:p-10 relative overflow-hidden animate-on-scroll border border-stone-200 shadow-card">
          <div className="absolute top-0 right-0 w-96 h-96 bg-primary/[0.04] rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-secondary/[0.04] rounded-full blur-3xl pointer-events-none" />
          {/* Animated ring */}
          <div className="absolute top-8 right-8 w-32 h-32 opacity-10 pointer-events-none">
            <div className="ring-rotate w-full h-full rounded-full border-2 border-dashed border-primary" />
          </div>

          <div className="relative z-10 grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="max-w-xl">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/25 text-primary text-[13px] font-bold uppercase tracking-widest mb-5">
                <span className="w-1.5 h-1.5 rounded-full bg-cta pulse-dot inline-block" />
                {lang === "id" ? "Multi Platform" : "Multi Platform"}
              </span>
              <h3 className="font-heading text-[31px] sm:text-[36px] font-bold text-fg leading-[1.16] tracking-[-0.02em] mb-4">
                {lang === "id" ? "Tersedia di Semua Platform" : "Available on All Platforms"}
              </h3>
              <p className="text-fg-muted text-[18px] leading-relaxed mb-6 sm:mb-7">
                {lang === "id"
                  ? "Kolabo hadir di Android, iOS, Windows Desktop, dan Web App. Akses dari mana saja, kapan saja."
                  : "Kolabo is available on Android, iOS, Windows Desktop, and Web App. Access it anywhere, anytime."}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3 mb-8 text-[15px] text-fg-muted">
                <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Sinkron realtime antar perangkat" : "Real-time sync across devices"}</p>
                <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Role akses sesuai jabatan" : "Role-based access control"}</p>
                <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Notifikasi tugas & approval" : "Task and approval notifications"}</p>
                <p className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-primary" />{lang === "id" ? "Data tersimpan terpusat" : "Centralized data storage"}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {platforms.map((p, i) =>
                <a
                  key={i}
                  href={p.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Buka ${p.label}`}
                  className="group rounded-2xl border border-stone-200 bg-white/90 backdrop-blur-sm px-4 py-3 min-h-[104px] flex flex-col items-center justify-center text-center shadow-sm hover:shadow-xl hover:-translate-y-0.5 hover:border-primary/35 transition-all duration-300">
                    <AppImage
                    src={p.icon}
                    alt={`${p.label} icon`}
                    width={220}
                    height={62}
                    className={`w-auto max-w-[210px] object-contain transition-transform duration-300 group-hover:scale-[1.02] ${p.logoClass}`}
                    sizes="220px" />
                    <span className="mt-1.5 text-[11px] font-semibold tracking-[0.14em] uppercase text-fg-muted/75">{p.sub}</span>
                  </a>
                )}
              </div>
            </div>

            <div className="relative lg:pl-4">
              <div className="relative rounded-[26px] border border-stone-200 bg-gradient-to-br from-slate-50 via-white to-teal-50/40 p-3 sm:p-4 shadow-2xl">
                <div className="relative h-[260px] sm:h-[310px] rounded-[18px] overflow-hidden border border-stone-200 bg-white">
                  <div className="h-9 px-3 border-b border-stone-200/85 bg-white/95 flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-rose-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-300" />
                    <span className="ml-2 text-[11px] text-fg-muted font-medium">app.kolabo.biz</span>
                  </div>
                  <div className="relative h-[calc(100%-36px)]">
                    <AppImage
                      src="/assets/optimized/kolabo-webapp-mockup-6-816746d7b7.webp"
                      alt="Kolabo web app tampilan dashboard lengkap"
                      fill
                      className="object-cover object-top"
                      sizes="(max-width: 1024px) 100vw, 50vw" />
                  </div>
                </div>
              </div>

              <div className="absolute bottom-2 left-2 sm:left-3 w-[132px] sm:w-[150px] rounded-[22px] border border-stone-200 bg-white p-2 shadow-xl">
                <div className="relative aspect-[9/19] rounded-[15px] overflow-hidden border border-stone-200 bg-stone-50">
                  <AppImage
                    src="/assets/optimized/kolabo-app-mockup-4-ef9e6fa852.webp"
                    alt="Kolabo mobile app preview"
                    fill
                    className="object-contain"
                    sizes="150px" />
                </div>
              </div>

              <div className="absolute top-3 right-3 sm:top-5 sm:right-5 rounded-full bg-white/95 backdrop-blur border border-stone-200 text-[11px] font-semibold text-fg px-3 py-1.5 shadow-sm">
                Real-time Sync
              </div>
            </div>
          </div>
        </div>

        {lightboxIdx !== null &&
        <div
          className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightboxIdx(null)}>
            <div className="relative w-full max-w-6xl bg-white rounded-xl overflow-hidden border border-stone-200" onClick={(e) => e.stopPropagation()}>
              <button
              type="button"
              onClick={() => setLightboxIdx(null)}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/95 border border-stone-200 text-fg hover:text-primary transition-colors">
                <XMarkIcon className="w-5 h-5 mx-auto" aria-hidden="true" />
              </button>
              <button
              type="button"
              onClick={() => setLightboxIdx((prev) => prev === null ? null : (prev - 1 + images.length) % images.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/95 border border-stone-200 text-fg hover:text-primary transition-colors"
              aria-label={lang === "id" ? "Gambar sebelumnya" : "Previous image"}>
                <ChevronLeftIcon className="w-5 h-5 mx-auto" aria-hidden="true" />
              </button>
              <button
              type="button"
              onClick={() => setLightboxIdx((prev) => prev === null ? null : (prev + 1) % images.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/95 border border-stone-200 text-fg hover:text-primary transition-colors"
              aria-label={lang === "id" ? "Gambar berikutnya" : "Next image"}>
                <ChevronRightIcon className="w-5 h-5 mx-auto" aria-hidden="true" />
              </button>
              <div
                className="relative w-full bg-stone-50 aspect-[16/9]"
              >
                <AppImage
                src={images[lightboxIdx].src}
                alt={images[lightboxIdx].alt}
                fill
                className="object-contain"
                sizes="100vw" />
              </div>
            </div>
          </div>
        }
      </div>
    </section>);

}
